import { useState, useEffect } from 'react'
import { apiService } from '@/service/api'
import { Loader2 } from 'lucide-react'
import { useTranslation } from '@/i18n'

interface SummaryTabProps {
  docName: string
  pageText: string | null
  docId?: string
  currentPage?: number
}

export function SummaryTab({ pageText, docId, currentPage }: SummaryTabProps) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [summary, setSummary] = useState<string>('')
  const [keyPoints, setKeyPoints] = useState<string[]>([])
  const [, setDocumentType] = useState<string>('')
  const [, setWordCount] = useState<number>(0)
  const [, setCharCount] = useState<number>(0)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isCancelled = false

    const fetchSummary = async () => {
      if (!docId || currentPage === undefined) {
        // Fallback to basic statistics if no doc ID
        if (pageText && !isCancelled) {
          const words = pageText.split(/\s+/).length
          const chars = pageText.length
          setSummary(t('document_viewer:summary.pageStats', { words, chars, defaultValue: 'This page contains {{words}} words and {{chars}} characters.' }))
          setWordCount(words)
          setCharCount(chars)
          setDocumentType('Document')
        }
        return
      }

      setLoading(true)
      setError(null)

      try {
        const data = await apiService.getDocumentPageSummary(docId, currentPage - 1)

        // Only update state if this request hasn't been cancelled
        if (!isCancelled) {
          setSummary(data.summary)
          setKeyPoints(data.key_points || [])
          setDocumentType(data.document_type || 'Document')
          setWordCount(data.word_count || 0)
          setCharCount(data.char_count || 0)
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('Failed to fetch summary:', err)
          setError(t('common:document_viewer.toasts.failed_to_generate_summary'))
          // Fallback to basic statistics
          if (pageText) {
            const words = pageText.split(/\s+/).length
            const chars = pageText.length
            setSummary(t('document_viewer:summary.pageStats', { words, chars, defaultValue: 'This page contains {{words}} words and {{chars}} characters.' }))
            setWordCount(words)
            setCharCount(chars)
          }
        }
      } finally {
        if (!isCancelled) {
          setLoading(false)
        }
      }
    }

    fetchSummary()

    return () => {
      isCancelled = true
    }
  }, [docId, currentPage, pageText])

  if (loading) {
    return (
      <div className="h-full overflow-auto bg-white dark:bg-dark-card p-6">
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm text-gray-600 dark:text-gray-400">{t('document_viewer:summary.generating', { defaultValue: 'Generating AI summary...' })}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto bg-white dark:bg-dark-card p-6">
      <div className="space-y-6">
        {/* AI-Generated Summary */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-4">{t('document_viewer:summary.title', { defaultValue: 'AI-Generated Summary' })}</h2>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            {summary || t('document_viewer:summary.noSummary', { defaultValue: 'No summary available' })}
          </p>
          {error && (
            <p className="text-xs text-red-500 mt-2">{error}</p>
          )}
        </div>

        {/* Key Points */}
        {keyPoints.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text mb-3">{t('document_viewer:summary.keyPoints', { defaultValue: 'Key Points' })}</h3>
            <ul className="space-y-2">
              {keyPoints.map((point, index) => (
                <li key={index} className="text-sm text-gray-700 dark:text-gray-300 flex items-start">
                  <span className="mr-2 text-primary">•</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
